if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      shouldSkipOptionValuesRender = false;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;
        this.shouldSkipOptionValuesRender = Boolean(target?.closest?.('[data-size-option]'));
        const normalizedOptionValues = Array.isArray(selectedOptionValues)
          ? selectedOptionValues.filter((value) => Boolean(value))
          : [];
        const selectedSizeValue = this.getSelectedSizeValue();
        const isSizeSelected = Boolean(selectedSizeValue);

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, normalizedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            const targetElement = document.querySelector(`#${targetId}`);
            if (!targetElement) return;
            const isMobile = window.matchMedia('(max-width: 989px)').matches;
            const isRadioInput = targetElement.tagName === 'INPUT' && targetElement.type === 'radio';
            // Mobile radio/swatches should not steal focus to avoid viewport jumps.
            if (isMobile && isRadioInput) return;
            try {
              targetElement.focus({ preventScroll: true });
            } catch (error) {
              targetElement.focus();
            }
          })
          .catch((error) => {
            if (error.name !== 'AbortError') {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getProductVariants() {
        const variantsJson = this.variantSelectors?.querySelector('[data-product-variants]')?.textContent?.trim();
        if (!variantsJson) return [];

        try {
          return JSON.parse(variantsJson);
        } catch (error) {
          console.error(error);
          return [];
        }
      }

      getOptionContainers() {
        return Array.from(this.variantSelectors?.querySelectorAll('.product-form__input') || []);
      }

      getSelectedOptionValueByIndex(optionIndex) {
        if (!Number.isInteger(optionIndex) || optionIndex < 0) return '';

        const optionContainer = this.getOptionContainers()[optionIndex];
        if (!optionContainer) return '';

        const checkedInput = optionContainer.querySelector('input[type="radio"]:checked');
        if (checkedInput?.value) return checkedInput.value;

        const select = optionContainer.querySelector('select.select__select');
        if (!select?.selectedOptions?.length) return '';
        return select.selectedOptions[0]?.value || '';
      }

      getColorOptionIndexForMedia() {
        return this.getOptionContainers().findIndex((container) => {
          if (container.hasAttribute('data-size-option')) return false;

          const optionLabel = container.querySelector('legend, label');
          const normalizedLabel = (optionLabel?.textContent || '').trim().toLowerCase();
          if (normalizedLabel.includes('color') || normalizedLabel.includes('colour') || normalizedLabel.includes('цвет')) {
            return true;
          }

          return (
            container.matches('.product-form__input--swatch') ||
            Boolean(container.querySelector('.swatch-input__input, [data-option-swatch-value]'))
          );
        });
      }

      getMediaVariantForSelectedColor() {
        const colorOptionIndex = this.getColorOptionIndexForMedia();
        const selectedColorValue = this.getSelectedOptionValueByIndex(colorOptionIndex).trim().toLowerCase();
        if (!selectedColorValue) return null;

        return (
          this.getProductVariants().find((variant) => {
            const variantColorValue = String(variant?.options?.[colorOptionIndex] || '')
              .trim()
              .toLowerCase();
            return variantColorValue === selectedColorValue && variant?.featured_media?.id;
          }) || null
        );
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const requestUrl = new URL(url, window.location.origin);
        const params = requestUrl.searchParams;

        if (!shouldFetchFullPage) {
          params.set('section_id', this.sectionId);
        } else {
          params.delete('section_id');
        }

        if (optionValues.length) {
          params.set('option_values', optionValues.join(','));
        } else {
          params.delete('option_values');
        }

        return `${requestUrl.pathname}?${params.toString()}`;
      }

      getSelectedSizeValue() {
        const checkedSizeInput = this.variantSelectors?.querySelector('input[data-size-option-input]:checked');
        if (checkedSizeInput?.value) return checkedSizeInput.value;

        const sizeSelect = this.variantSelectors?.querySelector('[data-size-option] select.select__select');
        if (!sizeSelect || !sizeSelect.selectedOptions?.length) return '';
        return sizeSelect.selectedOptions[0]?.value || '';
      }

      updateOptionValues(html) {
        const previouslySelectedSizeValue = this.getSelectedSizeValue();
        const shouldKeepSizeUnselected = this.requiresSizeSelection && !previouslySelectedSizeValue;

        if (this.shouldSkipOptionValuesRender) {
          this.shouldSkipOptionValuesRender = false;
          this.dispatchEvent(new CustomEvent('variant-picker:updated', { bubbles: true }));
          return;
        }

        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects && this.variantSelectors) {
          HTMLUpdateUtility.setInnerHTML(this.variantSelectors, variantSelects.innerHTML);
          if (shouldKeepSizeUnselected) {
            this.clearSizeSelection();
          } else {
            this.restoreSizeSelectionIfLost(previouslySelectedSizeValue);
          }
          this.dispatchEvent(new CustomEvent('variant-picker:updated', { bubbles: true }));
        }
      }

      clearSizeSelection() {
        const sizeOptionContainer = this.variantSelectors?.querySelector('[data-size-option]');
        if (!sizeOptionContainer) return;

        sizeOptionContainer.querySelectorAll('input[data-size-option-input]').forEach((input) => {
          input.checked = false;
          input.removeAttribute('checked');
        });

        const sizeSelect = sizeOptionContainer.querySelector('select.select__select');
        if (!sizeSelect) return;

        sizeSelect.value = '';
        Array.from(sizeSelect.options).forEach((option) => option.removeAttribute('selected'));
      }

      restoreSizeSelectionIfLost(previouslySelectedSizeValue) {
        if (!previouslySelectedSizeValue) return;
        if (this.getSelectedSizeValue()) return;

        const normalizedTarget = previouslySelectedSizeValue.trim().toLowerCase();
        if (!normalizedTarget) return;

        const sizeOptionContainer = this.variantSelectors?.querySelector('[data-size-option]');
        if (!sizeOptionContainer) return;

        const sizeInputs = Array.from(sizeOptionContainer.querySelectorAll('input[data-size-option-input]'));
        const matchingInput = sizeInputs.find((input) => {
          const normalizedValue = (input.value || '').trim().toLowerCase();
          return normalizedValue === normalizedTarget && !input.disabled;
        });

        if (matchingInput) {
          matchingInput.checked = true;
          return;
        }

        const sizeSelect = sizeOptionContainer.querySelector('select.select__select');
        if (!sizeSelect) return;

        const matchingOption = Array.from(sizeSelect.options).find((option) => {
          const normalizedValue = (option.value || '').trim().toLowerCase();
          return normalizedValue === normalizedTarget;
        });

        if (!matchingOption) return;
        sizeSelect.value = matchingOption.value;
        Array.from(sizeSelect.options).forEach((option) => option.removeAttribute('selected'));
        matchingOption.setAttribute('selected', 'selected');
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            const mediaVariant = this.getMediaVariantForSelectedColor();
            if (mediaVariant?.featured_media?.id) {
              this.updateMedia(html, mediaVariant.featured_media.id);
            }

            // Keep purchase state tied to a concrete variant selection.
            // Color-only selections may still update gallery media independently.

            const shouldKeepVariantUiActive = this.requiresSizeSelection && !this.getSelectedSizeValue();
            if (!shouldKeepVariantUiActive) {
              this.setUnavailable();
            }
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${url}`);

        if (this.dataset.updateUrl === 'false') return;
        const variant = variantId ? `?variant=${variantId}` : '';
        window.history.replaceState({}, '', `${url}${variant}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get requiresSizeSelection() {
        return this.productForm?.dataset?.requiresSizeSelection === 'true';
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
